import React from 'react';
import { Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './Card';
import { Badge } from './Badge';
import { Progress } from './Progress';
import { RatingStars } from './RatingStars';
import type { Expert } from '../types/expert';
import { averageRating, formatAED, formatDate } from '../utils/expert';
type OverviewPanelProps = {
  expert: Expert;
};
export function OverviewPanel({
  expert
}: OverviewPanelProps) {
  const rating = averageRating(expert);
  const distribution = [5, 4, 3, 2, 1].map(star => ({
    star,
    count: expert.reviews.filter(r => r.rating === star).length
  }));
  return <div className="grid gap-4 lg:grid-cols-[1fr_340px]" data-id="element-433" data-mp="21blnd3bhvx0e">
      <div className="flex flex-col gap-4" data-id="element-434" data-mp="26davaa3f7uk9">
        <Card data-id="element-435" data-mp="1rf8rbysh972h">
          <CardHeader data-id="element-436" data-mp="1reqo2i7edsl2">
            <CardTitle data-id="element-437" data-mp="1rga5ic4si9of">Fields of expertise</CardTitle>
            <CardDescription data-id="element-438" data-mp="1webybos0pnd3">Verified by BAK, plus possible fields inferred by AI from engagements and public activity.</CardDescription>
          </CardHeader>
          <CardContent data-id="element-439" data-mp="2s9uwg404qgnn">
            <ul className="space-y-4" data-id="element-440" data-mp="2sdip70xr2z1b">
              {expert.expertise.map(f => <li key={f.name} data-id="element-441" data-mp="2nc38tm6ipgp9">
                  <div className="flex items-center justify-between gap-2" data-id="element-442" data-mp="31bug742j3yua">
                    <span className="flex items-center gap-2 text-sm font-medium" data-id="element-443" data-mp="319rrqoh0ydro">
                      {f.name}
                      {f.source === 'AI suggested' ? <Badge variant="outline" className="gap-1 border-violet-200 bg-violet-50 text-violet-700" data-id="element-444" data-mp="29lg67ixmz6nw">
                          <Sparkles className="size-3" aria-hidden="true" data-id="element-445" data-mp="24jqtzzyogmy8" />AI suggested
                        </Badge> : <Badge variant="secondary" data-id="element-446" data-mp="2dm06uwhf4ses">Verified</Badge>}
                    </span>
                    <span className="text-xs tabular-nums text-muted-foreground" data-id="element-447" data-mp="1pvw1okol0klo">{f.confidence}% match</span>
                  </div>
                  <Progress value={f.confidence} className="mt-2" aria-label={`${f.name} confidence`} data-id="element-448" data-mp="1lv3tlurg4lqz" />
                </li>)}
            </ul>
          </CardContent>
        </Card>

        <Card data-id="element-449" data-mp="3pxds5vv72fup">
          <CardHeader data-id="element-450" data-mp="3pwu7w8i2nfdi">
            <CardTitle data-id="element-451" data-mp="3uq6wwjqnxo6s">Feedback & reviews</CardTitle>
            <CardDescription data-id="element-452" data-mp="3uqf6o2rhoddr">From organizers after each engagement.</CardDescription>
          </CardHeader>
          <CardContent data-id="element-453" data-mp="3iub1reu4mkj">
            <ul className="divide-y" data-id="element-454" data-mp="3219emgmgbre1">
              {expert.reviews.map(r => <li key={r.author + r.date} className="py-4 first:pt-0 last:pb-0" data-id="element-455" data-mp="323cahhaaceqo">
                  <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-456" data-mp="370kyvzm11f0d">
                    <div data-id="element-457" data-mp="3bxujn1yh12hv">
                      <p className="text-sm font-medium" data-id="element-458" data-mp="3c1iblwo9pguh">{r.author}</p>
                      <p className="text-xs text-muted-foreground" data-id="element-459" data-mp="3h03fc62qaino">{r.organization} · {formatDate(r.date)}</p>
                    </div>
                    <RatingStars value={r.rating} data-id="element-460" data-mp="rbj6duylxlvk" />
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground" data-id="element-461" data-mp="w6ggl57mi1a1">“{r.comment}”</p>
                </li>)}
            </ul>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col gap-4" data-id="element-462" data-mp="2brr3tfpzg4yc">
        <Card data-id="element-463" data-mp="270it8gcx2djl">
          <CardHeader data-id="element-464" data-mp="26zz9qv7mbh3g">
            <CardTitle data-id="element-465" data-mp="26zh8tla0gep7">Review score</CardTitle>
          </CardHeader>
          <CardContent data-id="element-466" data-mp="221pi2ul3qyml">
            <div className="flex items-end gap-3" data-id="element-467" data-mp="220ed4rfx7ktw">
              <span className="text-4xl font-semibold tabular-nums" data-id="element-468" data-mp="21zlao2nzvhhk">{rating.toFixed(1)}</span>
              <div className="pb-1" data-id="element-469" data-mp="1x002d1mxpws6">
                <RatingStars value={rating} data-id="element-470" data-mp="1t0ht534h7a56" />
                <p className="text-xs text-muted-foreground" data-id="element-471" data-mp="1szg8c6xnr9a9">{expert.reviews.length} reviews</p>
              </div>
            </div>
            <ul className="mt-4 space-y-1.5" data-id="element-472" data-mp="1j2tl5bhv0652">
              {distribution.map(d => <li key={d.star} className="flex items-center gap-2 text-xs" data-id="element-473" data-mp="3ifnxhosp4k5b">
                  <span className="w-3 tabular-nums text-muted-foreground" data-id="element-474" data-mp="3idl9tbf0n33r">{d.star}</span>
                  <div className="h-1.5 flex-1 rounded-full bg-muted" data-id="element-475" data-mp="3dhe15av8jdi4">
                    <div className="h-1.5 rounded-full bg-amber-400" style={{
                  width: `${expert.reviews.length ? d.count / expert.reviews.length * 100 : 0}%`
                }} data-id="element-476" data-mp="2b4844naskqkk" />
                  </div>
                  <span className="w-3 text-right tabular-nums text-muted-foreground" data-id="element-477" data-mp="217d94edq98b1">{d.count}</span>
                </li>)}
            </ul>
          </CardContent>
        </Card>

        <Card data-id="element-478" data-mp="1rdcspgczwgn0">
          <CardHeader data-id="element-479" data-mp="1w5w9em6zbrw1">
            <CardTitle data-id="element-480" data-mp="1w65xg48groz3">Cost</CardTitle>
          </CardHeader>
          <CardContent data-id="element-481" data-mp="1w7quq1m5xm0n">
            <dl className="space-y-3 text-sm" data-id="element-482" data-mp="1if16dqj5w50d">
              <div className="flex justify-between" data-id="element-483" data-mp="1igc9do4s98qf">
                <dt className="text-muted-foreground" data-id="element-484" data-mp="1nd2pgbgl84bu">Full day</dt>
                <dd className="font-semibold tabular-nums" data-id="element-485" data-mp="1nb81eoa0rw3j">{formatAED(expert.dailyRate)}</dd>
              </div>
              <div className="flex justify-between" data-id="element-486" data-mp="1dfeqf9by8anv">
                <dt className="text-muted-foreground" data-id="element-487" data-mp="1dewn5sqvcw6g">Half day / panel</dt>
                <dd className="font-semibold tabular-nums" data-id="element-488" data-mp="tsuukof5kfp1">{formatAED(expert.halfDayRate)}</dd>
              </div>
              <div className="flex justify-between" data-id="element-489" data-mp="ptcj0j981gyv">
                <dt className="text-muted-foreground" data-id="element-490" data-mp="13rspqa7337h0">VAT</dt>
                <dd className="text-muted-foreground" data-id="element-491" data-mp="13t3sq7spgb72">5% excluded</dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        <Card data-id="element-492" data-mp="1cs8s1gc3c8ka">
          <CardHeader data-id="element-493" data-mp="1crp7rsyyx833">
            <CardTitle data-id="element-494" data-mp="1cr5yf0ndvs0o">About</CardTitle>
          </CardHeader>
          <CardContent data-id="element-495" data-mp="18r5dqy9oykl6">
            <p className="text-sm text-muted-foreground" data-id="element-496" data-mp="18pu9kxcc3atj">{expert.bio}</p>
            <dl className="mt-4 space-y-2 text-sm" data-id="element-497" data-mp="1mi0l264jktmn">
              <div className="flex justify-between gap-4" data-id="element-498" data-mp="1mhii6qndjh5r">
                <dt className="text-muted-foreground" data-id="element-499" data-mp="1hixiqtfy51id">Nationality</dt>
                <dd data-id="element-500" data-mp="1wghinlxzk6ho">{expert.nationality}</dd>
              </div>
              <div className="flex justify-between gap-4" data-id="element-501" data-mp="1wgpv5cq759sc">
                <dt className="text-muted-foreground" data-id="element-502" data-mp="1ri6796s1lnxc">Languages</dt>
                <dd className="text-right" data-id="element-503" data-mp="1ykl65j2vpwvs">{expert.languages.join(', ')}</dd>
              </div>
              <div className="flex justify-between gap-4" data-id="element-504" data-mp="1yiqk21fvfyq4">
                <dt className="text-muted-foreground" data-id="element-505" data-mp="23kftxdrcy6cm">Member since</dt>
                <dd data-id="element-506" data-mp="28ihfc5c9ahr9">{formatDate(expert.memberSince)}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      </div>
    </div>;
}